import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import notify from 'Common/utils/notify';
import getEmployees from 'Employee/api/getEmployees';
import getInstructionAssignment from 'Education/api/instruction-assignment/getInstructionAssignment';
import updateGroupInstructionAssignment from 'Education/api/instruction-assignment/group/updateGroupInstructionAssignment';
import { useFormik } from 'formik';
import FormField from 'Common/components/Form/FormField';
import Select from 'Common/components/Form/Fields/Select';
import * as Yup from 'yup';
import useAxiosRequest from 'Common/hooks/useAxiosRequest';
import getInstruction from 'Education/api/instruction/getInstruction';
import RetryLoading from 'Common/components/RetryLoading';
import axios from 'axios';
import Modal from 'Common/components/Modals/Modal';
import Button from 'Common/components/Button';
import ModalTitle from 'Common/components/Modals/ModalTitle';
import { orderBy } from 'lodash';

const validationSchema = Yup.object().shape({
    groupLeader: Yup.object().required('Pflichtangabe').nullable(),
    employees: Yup.array()
        .of(Yup.object())
        .required('Pflichtangabe')
        .nullable(),
});

const GroupInstructionAssignmentEditModal = ({
    assignmentId,
    onCancel,
    onSuccess,
}) => {
    const [isSavingChanges, setIsSavingChanges] = useState(false);

    const isOpen = assignmentId !== null;

    const request = useCallback(
        async cancelToken => {
            const instructionAssignmentResponse = await getInstructionAssignment(
                assignmentId,
                ['responsibleEmployee', 'groupAssignmentDetails'],
                cancelToken,
            );

            const instructionResponse = await getInstruction(
                instructionAssignmentResponse.data.instructionId,
                ['areas'],
                cancelToken,
            );

            return {
                instructionAssignment: instructionAssignmentResponse.data,
                instruction: instructionResponse.data,
            };
        },
        [assignmentId],
    );

    const {
        data: { instructionAssignment, instruction },
        loadData: loadInstruction,
        isLoading: isLoadingInstruction,
        hasError: hasInstructionError,
    } = useAxiosRequest(request, {
        instructionAssignment: null,
        instruction: null,
    });

    const employeesRequest = useCallback(
        cancelToken =>
            getEmployees(
                ['areas', 'completedInstructions', 'assignedGroupInstructions'],
                cancelToken,
            ),
        [],
    );

    const {
        data: employees,
        loadData: loadEmployees,
        isLoading: isLoadingEmployees,
        hasError: hasEmployeesError,
    } = useAxiosRequest(employeesRequest, []);

    const initialValues = useMemo(() => {
        if (!instructionAssignment) {
            return {
                groupLeader: null,
                employees: [],
            };
        }

        const assignmentDetails = instructionAssignment.groupAssignmentDetails;
        const groupLeader = assignmentDetails.groupLeader;

        return {
            groupLeader: {
                value: groupLeader.employee.id,
                label: `${groupLeader.employee.lastName}, ${groupLeader.employee.firstName}`,
            },
            employees: assignmentDetails.assignees.map(assignee => ({
                value: assignee.employee.id,
                label: `${assignee.employee.lastName}, ${assignee.employee.firstName}`,
            })),
        };
    }, [instructionAssignment]);

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const {
        errors,
        touched,
        values,
        setFieldValue,
        setValues,
        handleBlur,
        handleSubmit,
        isValid,
    } = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema,
        onSubmit: async values => {
            setIsSavingChanges(true);

            try {
                await updateGroupInstructionAssignment(
                    assignmentId,
                    {
                        groupLeaderId: values.groupLeader.value,
                        employeeIds: values.employees.map(
                            option => option.value,
                        ),
                    },
                    source.token,
                );

                await onSuccess();

                setIsSavingChanges(false);

                notify('Der Präsenzunterweisung wurde aktualisiert', {
                    type: toast.TYPE.SUCCESS,
                });
            } catch (error) {
                if (!axios.isCancel(error)) {
                    setIsSavingChanges(false);

                    notify('Der Präsenzunterweisung wurde nicht aktualisiert', {
                        type: toast.TYPE.ERROR,
                    });
                }
            }
        },
    });

    const instructionId = instruction?.id ?? null;
    const groupLeaderId = values.groupLeader ? values.groupLeader.value : null;
    const selectedEmployeeOptions = values.employees ?? [];

    const employeesWithMatchingAreas = orderBy(
        employees.filter(employee => {
            if (! employee.isEnabled) {
                return false;
            }

            if (!instruction) {
                return false;
            }

            const instructionAreaIds = instruction.areas.map(area => area.id);
            const employeeAreaIds = employee.areas.map(area => area.id);
            const intersectingAreaIds = instructionAreaIds.filter(areaId =>
                employeeAreaIds.includes(areaId),
            );

            return intersectingAreaIds.length > 0;
        }),
        ['lastName', 'firstName'],
        ['asc', 'asc'],
    );

    const leaderEmployeeOptions = employeesWithMatchingAreas
        .filter(employee => !employee.isTrainee)
        .map(employee => ({
            value: employee.id,
            label: `${employee.lastName}, ${employee.firstName}`,
        }));

    const employeeOptions = employeesWithMatchingAreas
        .filter(employee => {
            const assignmentDetails =
                instructionAssignment.groupAssignmentDetails;
            const assignees = assignmentDetails.assignees;

            const savedEmployeeIds = assignees.map(
                assignee => assignee.employee.id,
            );

            const wasSavedPreviously = savedEmployeeIds.includes(employee.id);

            if (wasSavedPreviously) {
                return true;
            }

            const groupInstructionIds = employee.assignedGroupInstructions.map(
                instruction => instruction.id,
            );
            const completedInstructionIds = employee.completedInstructions.map(
                instruction => instruction.id,
            );

            const isPartOfAnotherGroupAssignmentOfThisInstruction = groupInstructionIds.includes(
                instructionId,
            );

            const hasCompletedInstruction = completedInstructionIds.includes(
                instructionId,
            );

            return (
                !isPartOfAnotherGroupAssignmentOfThisInstruction &&
                !hasCompletedInstruction
            );
        })
        .map(employee => ({
            value: employee.id,
            label: `${employee.lastName}, ${employee.firstName}`,
        }));

    const handleGroupLeaderChange = option => {
        const newEmployees = [...selectedEmployeeOptions];

        // Remove previously selected option
        if (groupLeaderId) {
            const currentlySelectedOptionIndex = selectedEmployeeOptions.findIndex(
                selectedOption => selectedOption.value === groupLeaderId,
            );

            if (currentlySelectedOptionIndex !== -1) {
                newEmployees.splice(currentlySelectedOptionIndex, 1);
            }
        }

        // Add new option
        if (option) {
            const optionIndex = employeeOptions.findIndex(
                employeeOption => employeeOption.value === option.value,
            );

            const isAlreadySelected =
                selectedEmployeeOptions.findIndex(
                    selectedOption => selectedOption.value === option.value,
                ) !== -1;

            if (optionIndex !== -1 && !isAlreadySelected) {
                newEmployees.push(option);
            }
        }

        setValues({
            ...values,
            groupLeader: option,
            employees: newEmployees,
        });
    };

    const isLoading = isLoadingInstruction || isLoadingEmployees;
    const hasError = hasInstructionError || hasEmployeesError;

    const handleRetryLoading = () => {
        loadInstruction();
        loadEmployees();
    };

    return (
        <Modal
            isOpen={isOpen}
            isLoading={isLoading}
            onRequestClose={isSavingChanges ? undefined : onCancel}
            buttons={
                !hasError && (
                    <>
                        <Button
                            text
                            dark
                            type="button"
                            onClick={onCancel}
                            disabled={isSavingChanges}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            type="button"
                            isLoading={isSavingChanges}
                            onClick={handleSubmit}
                            disabled={isSavingChanges || !isValid}
                        >
                            Speichern
                        </Button>
                    </>
                )
            }
        >
            {!isLoading &&
                (hasError ? (
                    <RetryLoading onRetry={handleRetryLoading} />
                ) : instructionAssignment === null ? (
                    <ModalTitle>
                        Der Präsenzunterweisung ist nicht mehr verfügbar
                    </ModalTitle>
                ) : (
                    <>
                        <ModalTitle>Präsenzunterweisung Bearbeiten</ModalTitle>
                        <p>{`Unterweisung: ${instruction.name}`}</p>
                        <form onSubmit={handleSubmit}>
                            <FormField>
                                <Select
                                    label="Gruppenleiter"
                                    id="groupLeader"
                                    name="groupLeader"
                                    error={errors.groupLeader}
                                    touched={touched.groupLeader}
                                    value={values.groupLeader}
                                    onChange={handleGroupLeaderChange}
                                    onBlur={handleBlur}
                                    options={leaderEmployeeOptions}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    isSearchable
                                    isClearable
                                />
                            </FormField>
                            <FormField>
                                <Select
                                    label="Mitarbeiter"
                                    id="employees"
                                    name="employees"
                                    error={errors.employees}
                                    touched={touched.employees}
                                    value={values.employees}
                                    setFieldValue={setFieldValue}
                                    onBlur={handleBlur}
                                    options={employeeOptions}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    isMulti
                                    isSearchable
                                    isClearable
                                />
                            </FormField>
                        </form>
                    </>
                ))}
        </Modal>
    );
};

GroupInstructionAssignmentEditModal.defaultProps = {
    assignmentId: null,
};

GroupInstructionAssignmentEditModal.propTypes = {
    assignmentId: PropTypes.number,
    onCancel: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
};

export default GroupInstructionAssignmentEditModal;
